import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCamera } from "../../context/CameraContext";
import StreamingDepthAggregator from "./StreamingDepthAggregator";

const DEFAULT_CONFIG = {
  frameIntervalMs: 200,
  targetFrames: 14,
  maxDurationMs: 5000,
  // Conservative backpressure: keep at most this many pending pairs in the
  // queue while a previous pair is still being analyzed. This reduces main
  // thread saturation and prevents the verifier from timing out with a full
  // backlog of stale work.
  maxPendingPairs: 2,
  // motionGate: optional. If set, the fixed interval is replaced by a
  // gyroscope-driven gate: a frame is captured only when the cumulative
  // device orientation delta (deg) exceeds motionGate.minDeltaDeg since
  // the last captured frame. The fixed timer still applies as a hard
  // ceiling (maxDurationMs) so the session always terminates.
  //
  // motionGate: {
  //   minDeltaDeg: 1.5,   // min orientation change in deg to trigger capture
  //   maxWaitMs: 3000,    // max ms to wait for motion before forcing a frame
  //   axis: 'gamma',      // 'gamma' = lateral tilt (best for left/right)
  // }
  motionGate: null,
};

const clamp01 = (v) => Math.max(0, Math.min(1, v));

// STEP 12 — Movement quality classifier (v2 — STEP 12b)
// Removed 'rotation_only': users should not be told HOW to move,
// only WHERE to point. Both rotation and translation produce parallax
// when the subject is close enough — distance is the real constraint.
//
// Categories:
//   too_fast    — camera moved excessively between frames (residual >> 150)
//   aim_closer  — good texture + motion but near-zero parallax: subject too far
//   good        — valid parallax window captured
//   null        — insufficient data (fallback, first frame, etc.)
const classifyMovementQuality = (analysisData, windowClass) => {
  const motionMean     = analysisData?.motionMean     ?? 0;
  const residualMean   = analysisData?.residualMean   ?? 0;
  const goodMatchCount = analysisData?.goodMatchCount ?? 0;

  // Too fast: camera moved so much the homography breaks down
  if (residualMean > 150 && motionMean > 80) return "too_fast";

  // Subject too far: texture present + motion detected + near-zero parallax.
  // Covers both rotation at distance and translation at distance.
  // goodMatchCount > 20 ensures enough features to make the judgment.
  if (goodMatchCount > 20 && residualMean < 2.0 && motionMean > 3) return "aim_closer";

  // Good window: positive classification or solid parallax in usable range
  if (windowClass === "positive" || (residualMean >= 2 && residualMean <= 150 && goodMatchCount >= 20)) {
    return "good";
  }

  return null;
};

const isValidPayload = (payload) =>
  !!payload &&
  typeof payload === "object" &&
  Object.prototype.hasOwnProperty.call(payload, "depthScore") &&
  Object.prototype.hasOwnProperty.call(payload, "outcome");

const DEBUG_PREFIX = "[DeepRealDebug]";

const debugLog = (scope, message, data) => {
  if (data === undefined) {
    console.log(`${DEBUG_PREFIX}[${scope}] ${message}`);
    return;
  }
  console.log(`${DEBUG_PREFIX}[${scope}] ${message}`, data);
};

const summarizeSnapshot = (snapshot) => ({
  tests: snapshot?.numberOfTests ?? 0,
  pos: snapshot?.positiveDepthTests ?? 0,
  neg: snapshot?.negativeDepthTests ?? 0,
  invalid: snapshot?.invalidTests ?? 0,
  screen: snapshot?.screenLikeSignals ?? 0,
  gray: snapshot?.grayZonePositives ?? 0,
  window: snapshot?.latestWindowClass ?? "unknown",
});

const logDecisionLine = (scope, data = {}) => {
  const snapshot = summarizeSnapshot(data?.snapshot);
  console.log(
    `${DEBUG_PREFIX}[${scope}] status=${data?.status ?? "unknown"} reason=${data?.reason ?? "unknown"} ` +
      `conf=${data?.confidence ?? 0} tests=${snapshot.tests} pos=${snapshot.pos} neg=${snapshot.neg} ` +
      `invalid=${snapshot.invalid} screen=${snapshot.screen} gray=${snapshot.gray} window=${snapshot.window}`
  );
};

const detectBrowserName = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const brands = Array.isArray(navigator.userAgentData?.brands)
    ? navigator.userAgentData.brands.map((entry) => entry.brand).join(" ")
    : "";
  const browserHints = `${brands} ${ua}`;

  if (typeof navigator.brave !== "undefined" || /Brave/i.test(browserHints)) return "Brave";
  if (/Microsoft Edge/i.test(browserHints) || /EdgA\//.test(ua) || /EdgiOS\//.test(ua) || /Edg\//.test(ua)) return "Edge";
  if (/Google Chrome/i.test(browserHints) || (/Chrome\//.test(ua) && !/EdgA\//.test(ua) && !/EdgiOS\//.test(ua) && !/Edg\//.test(ua))) return "Chrome";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "unknown";
};

const getProviderSnapshot = () => {
  if (typeof window === "undefined") {
    return {
      hasEthereum: false,
      providerCount: 0,
      providers: [],
    };
  }

  const ethereum = window.ethereum;
  const providers = Array.isArray(ethereum?.providers) && ethereum.providers.length
    ? ethereum.providers
    : ethereum
      ? [ethereum]
      : [];

  return {
    hasEthereum: !!ethereum,
    providerCount: providers.length,
    providers: providers.map((provider, index) => ({
      index,
      isMetaMask: provider?.isMetaMask === true,
      isBraveWallet: provider?.isBraveWallet === true,
      isCoinbaseWallet: provider?.isCoinbaseWallet === true,
      isWalletConnect:
        provider?.isWalletConnect === true ||
        provider?.session != null ||
        provider?.connector != null,
      hasRequest: typeof provider?.request === "function",
    })),
  };
};

const getEnvironmentSnapshot = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      browser: "unknown",
      secureContext: false,
    };
  }

  return {
    browser: detectBrowserName(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: Array.isArray(navigator.languages) ? navigator.languages : [],
    userAgentBrands: Array.isArray(navigator.userAgentData?.brands)
      ? navigator.userAgentData.brands
      : [],
    vendor: navigator.vendor,
    secureContext: window.isSecureContext === true,
    origin: window.location?.origin ?? "",
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      dpr: window.devicePixelRatio ?? 1,
    },
    screen: {
      width: window.screen?.width ?? 0,
      height: window.screen?.height ?? 0,
    },
    hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    deviceMemory: navigator.deviceMemory ?? null,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    visibilityState: document.visibilityState,
    hasNavigatorBrave: typeof navigator.brave !== "undefined",
    hasMediaDevices: !!navigator.mediaDevices,
    hasGetUserMedia: typeof navigator.mediaDevices?.getUserMedia === "function",
    hasDeviceOrientationEvent: typeof window.DeviceOrientationEvent !== "undefined",
    requiresDeviceOrientationPermission:
      typeof window.DeviceOrientationEvent?.requestPermission === "function",
    provider: getProviderSnapshot(),
  };
};

export default function useStreamingParallax({ enabled = false, videoElementRef, config = {} } = {}) {
  const settings = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);

  const {
    startCamera,
    attachToVideo,
    setPrimaryVideoEl,
    primaryVideoRef,
    isVideoReady,
    isActive: cameraActive,
  } = useCamera();

  const [phase, setPhase] = useState("idle");
  const [instruction, setInstruction] = useState("Move your phone slowly left and right to verify scene depth");
  const [progress, setProgress] = useState(0);
  const [confidence, setConfidence] = useState(0);
  const [summary, setSummary] = useState({
    numberOfTests: 0,
    positiveDepthTests: 0,
    negativeDepthTests: 0,
    invalidTests: 0,
    screenLikeSignals: 0,
    stableScoreStdDev: 0,
    latestDepthScore: null,
    latestOutcome: null,
    latestReasons: [],
  });
  const [analysisPair, setAnalysisPair] = useState(null);
  const [analysisKey, setAnalysisKey] = useState(0);
  const [uploadFrames, setUploadFrames] = useState(null);
  const [bestFrame, setBestFrame] = useState(null);   // STEP 11 — thumbnail del frame con score più alto
  const [movementWarning, setMovementWarning] = useState(null); // STEP 12 — real-time movement quality feedback
  const [blockReason, setBlockReason] = useState("Verification not completed.");

  const aggregatorRef = useRef(new StreamingDepthAggregator());
  const orientationRef = useRef({ absolute: false, alpha: null, beta: null, gamma: null });

  const lastFrameRef = useRef(null);
  const lastOrientationRef = useRef(null);
  const captureCountRef = useRef(0);
  const pairIdRef = useRef(0);
  const queueRef = useRef([]);
  const activePairRef = useRef(null);
  const lastAnalyzedPairRef = useRef(null);
  const bestScoredPairRef = useRef(null);   // STEP 11 — pair con depthScore massimo
  const bestScoreRef = useRef(-Infinity);   // STEP 11 — score massimo visto finora
  const clearWarningTimerRef = useRef(null); // STEP 12 — auto-clear timer for "good" flash
  const analyzingRef = useRef(false);
  const captureTimerRef = useRef(null);
  const timeoutRef = useRef(null);
  const stoppedByTimeoutRef = useRef(false);
  const captureDoneRef = useRef(false);

  const cleanupTimers = useCallback(() => {
    if (captureTimerRef.current) {
      clearInterval(captureTimerRef.current);
      captureTimerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Stop motion-gate rAF loop if running
    if (motionGateRafRef.current) {
      cancelAnimationFrame(motionGateRafRef.current);
      motionGateRafRef.current = null;
    }
  }, []); // motionGateRafRef is a ref — stable, safe to omit from deps

  const resetSession = useCallback(() => {
    debugLog("Session", "resetSession");
    cleanupTimers();
    aggregatorRef.current.reset();
    queueRef.current = [];
    activePairRef.current = null;
    lastAnalyzedPairRef.current = null;
    analyzingRef.current = false;
    lastFrameRef.current = null;
    lastOrientationRef.current = null;
    captureCountRef.current = 0;
    pairIdRef.current = 0;
    stoppedByTimeoutRef.current = false;
    captureDoneRef.current = false;
    setSummary(aggregatorRef.current.getSnapshot());
    setAnalysisPair(null);
    setProgress(0);
    setConfidence(0);
    setUploadFrames(null);
    setBestFrame(null);
    bestScoredPairRef.current = null;
    bestScoreRef.current = -Infinity;
    setMovementWarning(null);  // STEP 12
    if (clearWarningTimerRef.current) {
      clearTimeout(clearWarningTimerRef.current);
      clearWarningTimerRef.current = null;
    }
    setBlockReason("Verification not completed.");
  }, [cleanupTimers]);

  const finalizeFromDecision = useCallback((decision, currentPair = null) => {
    debugLog("Finalize", "finalizeFromDecision", {
      status: decision?.status,
      reason: decision?.reason,
      confidence: decision?.confidence,
      currentPairId: currentPair?.id ?? null,
      bestPairId: bestScoredPairRef.current?.id ?? null,
      captureDone: captureDoneRef.current,
      queueLength: queueRef.current.length,
      analyzing: analyzingRef.current,
      snapshot: {
        numberOfTests: decision?.snapshot?.numberOfTests,
        positiveDepthTests: decision?.snapshot?.positiveDepthTests,
        negativeDepthTests: decision?.snapshot?.negativeDepthTests,
        invalidTests: decision?.snapshot?.invalidTests,
        screenLikeSignals: decision?.snapshot?.screenLikeSignals,
        grayZonePositives: decision?.snapshot?.grayZonePositives,
      },
    });
    logDecisionLine("Finalize", decision);
    setConfidence(decision.confidence || 0);
    setSummary(decision.snapshot);

    if (decision.status === "pass") {
      debugLog("Finalize", "setting phase=pass");
      setPhase("pass");
      setInstruction("Depth verified.");
      setProgress(1);
      setBlockReason("");
      // STEP 11 — usa il pair con score massimo, non l'ultimo analizzato
      const selectedPair = bestScoredPairRef.current || currentPair || activePairRef.current || lastAnalyzedPairRef.current;
      if (selectedPair) {
        setUploadFrames([selectedPair.frame1, selectedPair.frame2]);
        setBestFrame(selectedPair.frame1);
      }
      cleanupTimers();
      return true;
    }

    if (decision.status === "fail") {
      debugLog("Finalize", "setting phase=fail");
      setPhase("fail");
      setInstruction("Scene appears flat or screen-like.");
      setProgress(1);
      setBlockReason("Depth verification failed: scene appears flat or screen-like.");
      cleanupTimers();
      return true;
    }

    if (decision.status === "retry") {
      debugLog("Finalize", "setting phase=retry");
      setPhase("retry");
      setInstruction("Please move the phone more slowly and try again.");
      setProgress(1);
      setBlockReason("Depth evidence was insufficient. Please retry the motion challenge.");
      cleanupTimers();
      return true;
    }

    return false;
  }, [cleanupTimers]);

  const maybeFinalizeTimeout = useCallback(() => {
    if (!captureDoneRef.current) return;
    if (analyzingRef.current) {
      debugLog("Timeout", "finalization deferred: analysis still running", {
        queueLength: queueRef.current.length,
      });
      return;
    }
    if (queueRef.current.length > 0) {
      debugLog("Timeout", "finalization deferred: queue not empty", {
        queueLength: queueRef.current.length,
      });
      return;
    }

    const decision = aggregatorRef.current.evaluate({ timeout: true });
    debugLog("Timeout", "evaluate(timeout=true)", {
      status: decision?.status,
      reason: decision?.reason,
      confidence: decision?.confidence,
      snapshot: {
        numberOfTests: decision?.snapshot?.numberOfTests,
        positiveDepthTests: decision?.snapshot?.positiveDepthTests,
        negativeDepthTests: decision?.snapshot?.negativeDepthTests,
        invalidTests: decision?.snapshot?.invalidTests,
        screenLikeSignals: decision?.snapshot?.screenLikeSignals,
        grayZonePositives: decision?.snapshot?.grayZonePositives,
      },
    });
    logDecisionLine("Timeout", decision);
    finalizeFromDecision(decision);
  }, [finalizeFromDecision]);

  const runNextAnalysis = useCallback(() => {
    if (analyzingRef.current) return;
    if (queueRef.current.length === 0) {
      debugLog("Queue", "runNextAnalysis with empty queue", {
        captureDone: captureDoneRef.current,
      });
      maybeFinalizeTimeout();
      return;
    }

    const nextPair = queueRef.current.shift();
    debugLog("Queue", "dequeued analysis pair", {
      pairId: nextPair?.id ?? null,
      queueLengthAfterShift: queueRef.current.length,
    });
    activePairRef.current = nextPair;
    analyzingRef.current = true;
    setAnalysisKey((k) => k + 1);
    setAnalysisPair(nextPair);
  }, [maybeFinalizeTimeout]);

  const stopCapture = useCallback(({ timeout = false } = {}) => {
    debugLog("Capture", "stopCapture", {
      timeout,
      captureCount: captureCountRef.current,
      queueLength: queueRef.current.length,
      analyzing: analyzingRef.current,
    });
    cleanupTimers();
    captureDoneRef.current = true;
    stoppedByTimeoutRef.current = timeout;

    if (!analyzingRef.current && queueRef.current.length > 0) {
      debugLog("Capture", "draining remaining analysis queue after stop", {
        queueLength: queueRef.current.length,
      });
      runNextAnalysis();
      return;
    }

    maybeFinalizeTimeout();
  }, [cleanupTimers, maybeFinalizeTimeout, runNextAnalysis]);

  // lastCaptureOrientationRef: orientation snapshot at the time of the last
  // captured frame — used by the motion-gate loop to measure cumulative delta.
  const lastCaptureOrientationRef = useRef(null);
  // motionGateRafRef: rAF handle for the motion-gate loop.
  const motionGateRafRef = useRef(null);
  // lastMotionWaitStartRef: timestamp when we started waiting for motion.
  const lastMotionWaitStartRef = useRef(null);
  // lastCaptureTimestampRef: throttles motion-gate captures so wrist jitter
  // cannot produce back-to-back samples a few rAFs apart.
  const lastCaptureTimestampRef = useRef(0);

  const captureFrame = useCallback(() => {
    const video = primaryVideoRef?.current;
    if (!video || !(isVideoReady?.(video) || (video.videoWidth > 0 && video.videoHeight > 0))) {
      debugLog("Capture", "capture skipped: video not ready");
      return;
    }

    if (queueRef.current.length >= settings.maxPendingPairs) {
      debugLog("Capture", "capture skipped: queue saturated", {
        queueLength: queueRef.current.length,
        maxPendingPairs: settings.maxPendingPairs,
        analyzing: analyzingRef.current,
      });
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    const frame = canvas.toDataURL("image/jpeg", 0.85);
    const orientation = { ...orientationRef.current };

    if (lastFrameRef.current) {
      const pair = {
        id: ++pairIdRef.current,
        frame1: lastFrameRef.current,
        frame2: frame,
        orientations: [lastOrientationRef.current, orientation],
      };
      queueRef.current.push(pair);
      if (queueRef.current.length > 6) {
        queueRef.current.shift();
      }
    }

    lastFrameRef.current = frame;
    lastOrientationRef.current = orientation;
    // Update motion-gate baseline to current orientation.
    lastCaptureOrientationRef.current = { ...orientation };
    const now = performance.now();
    lastMotionWaitStartRef.current = now;
    lastCaptureTimestampRef.current = now;

    captureCountRef.current += 1;
    setProgress(clamp01(captureCountRef.current / settings.targetFrames));
    debugLog("Capture", "frame captured", {
      captureCount: captureCountRef.current,
      targetFrames: settings.targetFrames,
      queueLength: queueRef.current.length,
      lastPairId: pairIdRef.current || null,
    });

    if (captureCountRef.current >= settings.targetFrames) {
      stopCapture({ timeout: false });
    } else {
      runNextAnalysis();
    }
  }, [primaryVideoRef, isVideoReady, settings.maxPendingPairs, settings.targetFrames, stopCapture, runNextAnalysis]);

  // ── Motion-gate loop ──────────────────────────────────────────────
  // Replaces setInterval when settings.motionGate is configured.
  // Runs at ~60fps via rAF, fires captureFrame() only when:
  //   a) cumulative orientation delta >= minDeltaDeg  (motion detected)
  //   b) maxWaitMs has elapsed since last capture     (force fallback)
  const startMotionGateLoop = useCallback((gate) => {
    const {
      minDeltaDeg = 1.5,
      maxWaitMs = 3000,
      axis = 'gamma',
      minCaptureIntervalMs = settings.frameIntervalMs,
    } = gate;

    lastCaptureOrientationRef.current = { ...orientationRef.current };
    const startTs = performance.now();
    lastMotionWaitStartRef.current = startTs;
    lastCaptureTimestampRef.current = startTs;

    const loop = () => {
      if (captureDoneRef.current) return;

      const now  = performance.now();
      const cur  = orientationRef.current;
      const base = lastCaptureOrientationRef.current;

      let delta = 0;
      if (base && cur[axis] !== null && base[axis] !== null) {
        delta = Math.abs(cur[axis] - base[axis]);
        if (delta > 180) delta = 360 - delta; // wrap-around safe
      }

      const waitedMs        = now - (lastMotionWaitStartRef.current ?? now);
      const elapsedSinceCapture = now - (lastCaptureTimestampRef.current ?? 0);
      const motionTriggered = delta >= minDeltaDeg;
      const timerTriggered  = waitedMs >= maxWaitMs;
      const captureCooldownSatisfied = elapsedSinceCapture >= minCaptureIntervalMs;

      if ((motionTriggered || timerTriggered) && captureCooldownSatisfied) {
        if (queueRef.current.length >= settings.maxPendingPairs) {
          debugLog("Capture", "motion-gate backpressure", {
            queueLength: queueRef.current.length,
            maxPendingPairs: settings.maxPendingPairs,
            analyzing: analyzingRef.current,
            waitedMs: Math.round(waitedMs),
            elapsedSinceCapture: Math.round(elapsedSinceCapture),
            deltaDeg: Number(delta.toFixed(2)),
          });
          // Treat the skipped event as a consumed gate tick so we don't keep
          // hammering the producer every frame while analysis is behind.
          lastCaptureOrientationRef.current = { ...cur };
          lastMotionWaitStartRef.current = now;
          motionGateRafRef.current = requestAnimationFrame(loop);
          return;
        }

        const reason = motionTriggered
          ? `Δ${axis}=${delta.toFixed(2)}°`
          : `timeout ${Math.round(waitedMs)}ms`;
        console.log(`[MotionGate] capture — ${reason}`);
        captureFrame();
        // lastCaptureOrientationRef and lastMotionWaitStartRef are
        // updated inside captureFrame() — no reset needed here.
      }

      motionGateRafRef.current = requestAnimationFrame(loop);
    };

    motionGateRafRef.current = requestAnimationFrame(loop);
  }, [captureFrame, settings.maxPendingPairs]);

  const stopMotionGateLoop = useCallback(() => {
    if (motionGateRafRef.current) {
      cancelAnimationFrame(motionGateRafRef.current);
      motionGateRafRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    if (!enabled) return;

    debugLog("Env", "browser snapshot", getEnvironmentSnapshot());
    debugLog("Session", "start invoked", {
      targetFrames: settings.targetFrames,
      maxDurationMs: settings.maxDurationMs,
      motionGate: settings.motionGate,
    });
    resetSession();
    setPhase("running");
    setInstruction("Move your phone slowly left and right to verify scene depth");

    try {
      await startCamera();
    } catch (e) {
      setPhase("error");
      setBlockReason(`Camera start failed: ${e?.message || String(e)}`);
      return;
    }

    const videoEl = videoElementRef?.current;
    if (videoEl) {
      setPrimaryVideoEl?.(videoEl);
      attachToVideo?.(videoEl);
    }

    // Hard ceiling: session always ends after maxDurationMs regardless of mode.
    debugLog("Timeout", "hard timeout armed", {
      maxDurationMs: settings.maxDurationMs,
    });
    timeoutRef.current = setTimeout(() => {
      debugLog("Timeout", "hard timeout fired", {
        captureCount: captureCountRef.current,
        queueLength: queueRef.current.length,
        analyzing: analyzingRef.current,
      });
      stopCapture({ timeout: true });
    }, settings.maxDurationMs);

    if (settings.motionGate) {
      // Motion-driven mode: capture only when gyroscope detects movement.
      console.log('[useStreamingParallax] Motion-gate mode active:', settings.motionGate);
      startMotionGateLoop(settings.motionGate);
    } else {
      // Legacy time-driven mode: capture at fixed intervals.
      captureTimerRef.current = setInterval(captureFrame, settings.frameIntervalMs);
    }
  }, [enabled, resetSession, startCamera, videoElementRef, setPrimaryVideoEl, attachToVideo, captureFrame, settings.frameIntervalMs, settings.maxDurationMs, settings.motionGate, stopCapture, startMotionGateLoop]);

  const retry = useCallback(() => {
    start();
  }, [start]);

  const handleAnalysisComplete = useCallback((payload) => {
    const normalizedPayload = isValidPayload(payload)
      ? payload
      : {
          outcome: "invalid_payload",
          depthScore: -1,
          confidence: 0,
          reason: "Malformed payload from AnalyzeDepthFromFrames",
          analysisData: { depthConsistency: null },
        };

    const justAnalyzedPair = activePairRef.current;
    if (justAnalyzedPair) {
      lastAnalyzedPairRef.current = justAnalyzedPair;
    }
    analyzingRef.current = false;
    activePairRef.current = null;
    setAnalysisPair(null);

    const snapshot = aggregatorRef.current.ingest(normalizedPayload);
    setSummary(snapshot);
    debugLog("Analysis", "analysis complete", {
      pairId: justAnalyzedPair?.id ?? null,
      outcome: normalizedPayload?.outcome,
      depthScore: normalizedPayload?.depthScore,
      captureQuality: normalizedPayload?.captureQuality ?? null,
      snapshot: {
        numberOfTests: snapshot?.numberOfTests,
        positiveDepthTests: snapshot?.positiveDepthTests,
        negativeDepthTests: snapshot?.negativeDepthTests,
        invalidTests: snapshot?.invalidTests,
        screenLikeSignals: snapshot?.screenLikeSignals,
        latestWindowClass: snapshot?.latestWindowClass,
        grayZonePositives: snapshot?.grayZonePositives,
      },
      queueLength: queueRef.current.length,
    });
    console.log(
      `${DEBUG_PREFIX}[Analysis] pair=${justAnalyzedPair?.id ?? "?"} outcome=${normalizedPayload?.outcome ?? "unknown"} ` +
        `score=${normalizedPayload?.depthScore ?? "?"} quality=${normalizedPayload?.captureQuality ?? "unknown"} ` +
        `window=${snapshot?.latestWindowClass ?? "unknown"} tests=${snapshot?.numberOfTests ?? 0} ` +
        `pos=${snapshot?.positiveDepthTests ?? 0} neg=${snapshot?.negativeDepthTests ?? 0} ` +
        `invalid=${snapshot?.invalidTests ?? 0} screen=${snapshot?.screenLikeSignals ?? 0} gray=${snapshot?.grayZonePositives ?? 0}`
    );

    // STEP 11 — aggiorna il pair con score migliore per thumbnail e upload
    const ingestedScore = normalizedPayload?.depthScore ?? -1;
    if (ingestedScore > bestScoreRef.current && justAnalyzedPair) {
      bestScoreRef.current = ingestedScore;
      bestScoredPairRef.current = justAnalyzedPair;
      setBestFrame(justAnalyzedPair.frame1);
    }

    // STEP 12 — movement quality classification
    const movementClass = classifyMovementQuality(
      normalizedPayload?.analysisData,
      snapshot.latestWindowClass
    );
    if (movementClass === "good") {
      setMovementWarning("good");
      if (clearWarningTimerRef.current) clearTimeout(clearWarningTimerRef.current);
      clearWarningTimerRef.current = setTimeout(() => setMovementWarning(null), 2000);
    } else if (movementClass !== null) {
      // Persistent warning (too_fast, rotation_only, aim_closer) — stays until next window
      if (clearWarningTimerRef.current) {
        clearTimeout(clearWarningTimerRef.current);
        clearWarningTimerRef.current = null;
      }
      setMovementWarning(movementClass);
    }
    // null — no update: keep last warning visible

    const decision = aggregatorRef.current.evaluate({ timeout: false });
    debugLog("Analysis", "evaluate(timeout=false)", {
      status: decision?.status,
      reason: decision?.reason,
      confidence: decision?.confidence,
      snapshot: {
        numberOfTests: decision?.snapshot?.numberOfTests,
        positiveDepthTests: decision?.snapshot?.positiveDepthTests,
        negativeDepthTests: decision?.snapshot?.negativeDepthTests,
        invalidTests: decision?.snapshot?.invalidTests,
        screenLikeSignals: decision?.snapshot?.screenLikeSignals,
        grayZonePositives: decision?.snapshot?.grayZonePositives,
      },
    });
    logDecisionLine("Decision", decision);
    const done = finalizeFromDecision(decision, justAnalyzedPair);

    if (!done) {
      runNextAnalysis();
    }
  }, [finalizeFromDecision, runNextAnalysis]);

  useEffect(() => {
    debugLog("Phase", "phase updated", { phase });
  }, [phase]);

  useEffect(() => {
    if (!enabled) {
      resetSession();
      setPhase("idle");
      return;
    }
  }, [enabled, resetSession]);

  useEffect(() => {
    const handleOrientation = (event) => {
      orientationRef.current = {
        absolute: event.absolute,
        alpha: event.alpha,
        beta: event.beta,
        gamma: event.gamma,
      };
    };
    window.addEventListener("deviceorientation", handleOrientation);
    return () => window.removeEventListener("deviceorientation", handleOrientation);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const videoEl = videoElementRef?.current;
    if (!videoEl) return;
    setPrimaryVideoEl?.(videoEl);
    attachToVideo?.(videoEl);
  }, [enabled, videoElementRef, setPrimaryVideoEl, attachToVideo, cameraActive]);

  useEffect(() => () => cleanupTimers(), [cleanupTimers]);

  return {
    phase,
    instruction,
    progress,
    confidence,
    summary,
    analysisPair,
    analysisKey,
    handleAnalysisComplete,
    start,
    retry,
    uploadFrames,
    blockReason,
    bestFrame,
    movementWarning,  // STEP 12
    canUpload: phase === "pass" && Array.isArray(uploadFrames) && uploadFrames.length === 2,
  };
}
